import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { MeResponse, UserRole } from '../models/portal.models';

const TOKEN_KEY = 'driveme.auth.token';
const USER_KEY = 'driveme.auth.user';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly router = inject(Router);

  private readonly tokenState = signal<string | null>(readStoredValue(TOKEN_KEY));
  private readonly userState = signal<MeResponse | null>(readStoredUser());

  readonly token = computed(() => this.tokenState());
  readonly currentUser = computed(() => this.userState());

  isAuthenticated(): boolean {
    return Boolean(this.tokenState());
  }

  hasAnyRole(roles: UserRole[]): boolean {
    const user = this.userState();
    return Boolean(user && roles.includes(user.role));
  }

  setToken(token: string, rememberMe = true): void {
    const normalizedToken = token.trim();
    const storage = getAuthStorage(rememberMe);
    storage.setItem(TOKEN_KEY, normalizedToken);
    clearOtherStorage(storage, TOKEN_KEY);
    this.tokenState.set(normalizedToken);
  }

  setCurrentUser(user: MeResponse, rememberMe = true): void {
    const storage = getAuthStorage(rememberMe);
    storage.setItem(USER_KEY, JSON.stringify(user));
    clearOtherStorage(storage, USER_KEY);
    this.userState.set(user);
  }

  logout(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(USER_KEY);
    this.tokenState.set(null);
    this.userState.set(null);
    this.router.navigateByUrl('/login');
  }
}

function getAuthStorage(rememberMe: boolean): Storage {
  return rememberMe ? localStorage : sessionStorage;
}

function clearOtherStorage(activeStorage: Storage, key: string): void {
  const otherStorage = activeStorage === localStorage ? sessionStorage : localStorage;
  otherStorage.removeItem(key);
}

function readStoredValue(key: string): string | null {
  return sessionStorage.getItem(key) ?? localStorage.getItem(key);
}

function readStoredUser(): MeResponse | null {
  const raw = readStoredValue(USER_KEY);

  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as MeResponse;
  } catch {
    localStorage.removeItem(USER_KEY);
    sessionStorage.removeItem(USER_KEY);
    return null;
  }
}
